import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useHotelDetails } from '../../hooks/useHotelDetails';
import { useBookingSession } from '../../hooks/useBookingSession';
import LoadingSpinner from '../common/LoadingSpinner';
import SessionExpiredModal from '../common/SessionExpiredModal';
import PhotoGallery from './hotel-details/PhotoGallery';
import HotelOverview from './hotel-details/HotelOverview';
import RoomDetails from './hotel-details/RoomDetails';
import HotelFacilities from './hotel-details/HotelFacilities';
import BookingTimer from './hotel-details/BookingTimer';
import ProfileCompletionModal from './hotel-details/ProfileCompletionModal';

const HotelDetailsPage = () => {
  const { hotelId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const provider = searchParams.get('provider');
  const searchId = searchParams.get('searchId');

  const {
    loading,
    hotelDetails,
    isProfileComplete,
    timerKey,
    expiryTime,
    roomWiseCancellationPolicy,
    error,
    refetch,
    hotelDetailsKey, // Redis key for hotelDetails
  } = useHotelDetails(hotelId, provider, searchId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Track booking session expiry with modal trigger
  const { isExpired: isSessionExpired, hasValidSession, getTimeRemaining } = useBookingSession(
    expiryTime, 
    () => setShowSessionExpiredModal(true) // Show modal when session expires
  );

  // Handle scroll for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to section when tab changes
  useEffect(() => {
    if (activeTab) {
      const element = document.getElementById(activeTab);
      if (element) {
        const headerOffset = 120; // Account for sticky header
        const elementPosition = element.offsetTop;
        const offsetPosition = elementPosition - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [activeTab]);

  // Handle scroll spy for tabs
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'info-prices', 'facilities'];
      const scrollPosition = window.pageYOffset + 150;

      for (const sectionId of sections) {
        const section = document.getElementById(sectionId);
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionBottom = sectionTop + section.offsetHeight;

          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            setActiveTab(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBookNow = (roomDetails) => {
    console.log('🎯 Book Now clicked');
    console.log('📊 Profile Status:', { isProfileComplete });
    console.log('⏰ Session Status:', { isSessionExpired, hasValidSession });
    
    // Check if session is expired - prevent all navigation
    if (isSessionExpired || !hasValidSession) {
      console.log('❌ Session expired - showing modal');
      setShowSessionExpiredModal(true);
      return;
    }

    if (!isProfileComplete) {
      console.log('⚠️ Profile incomplete - showing profile modal');
      setShowProfileModal(true);
      return;
    }

    console.log('✅ All checks passed - proceeding to booking');
    console.log('🔑 Redis hotelDetailsKey:', hotelDetailsKey);

    // Get roomAllocations from URL params, or create a default one
    let roomAllocationsParam = searchParams.get('roomAllocations') || '';
    
    // If roomAllocations is missing, create a default allocation (1 room, 2 adults, 0 children)
    if (!roomAllocationsParam) {
      try {
        const defaultRoomAllocation = [{
          roomNumber: 1,
          adults: 2,
          children: 0,
          childAges: []
        }];
        const json = JSON.stringify(defaultRoomAllocation);
        roomAllocationsParam = btoa(unescape(encodeURIComponent(json)));
        console.log('Created default room allocation:', roomAllocationsParam);
      } catch (error) {
        console.error('Error creating default room allocation:', error);
        roomAllocationsParam = '';
      }
    }

    // Create URL parameters for prebook customer details page
    const prebookParams = new URLSearchParams({
      roomBookingCode: roomDetails.roomBookingCode,
      searchSessionId: hotelDetails?.searchSessionId || '',
      hotelId: hotelId,
      destination: hotelDetails?.overview?.address || '',
      checkInDate: searchParams.get('checkInDate') || '',
      checkOutDate: searchParams.get('checkOutDate') || '',
      roomAllocations: roomAllocationsParam,
      provider: provider,
      timerKey: expiryTime, // Pass expiryTime as timerKey (remove separate expiryTime param)
      roomPrice: roomDetails.roomPrice,
      isRefundable: roomDetails.impDetails?.isRefundable || false,
      hotelDetailsKey: hotelDetailsKey || '', // Pass Redis key to retrieve hotelDetails
    });

    // Open prebook customer details page in new tab
    const prebookUrl = `/agent/dashboard/prebook/customer-detail/${hotelId}?${prebookParams.toString()}`;
    
    console.log('🔍 Opening customer details page with hotelDetailsKey:', hotelDetailsKey);
    console.log('📋 Full URL:', prebookUrl);
    
    window.open(prebookUrl, '_blank', 'noopener,noreferrer');
  };

  const handleProfileComplete = () => {
    navigate('/agent/profile/update/method');
  };

  const handleProfileCompleted = () => {
    console.log('✅ Profile completed successfully - refreshing hotel details');
    // Refresh hotel details to get updated isProfileComplete status
    refetch();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2L3 7v11a2 2 0 002 2h4v-6h2v6h4a2 2 0 002-2V7l-7-5z" />
              </svg>
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Loading Hotel Details</h3>
          <p className="text-gray-600">Please wait while we fetch the latest information...</p>
          <div className="mt-4 flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="relative mb-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Oops! Something went wrong</h3>
          <p className="text-gray-600 mb-6 leading-relaxed">{error}</p>
          <div className="space-y-3">
            <button 
              onClick={refetch}
              className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-red-600 hover:to-pink-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
            <button 
              onClick={() => window.close()}
              className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Close Window
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hotelDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="relative mb-6">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-10 h-10 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">No Hotel Details Available</h3>
          <p className="text-gray-600 mb-6 leading-relaxed">
            We couldn't find any hotel information for your selected criteria. This might be due to availability or search parameters.
          </p>
          <div className="space-y-3">
            <button 
              onClick={() => window.history.back()}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-yellow-600 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Go Back & Search Again
            </button>
            <button 
              onClick={() => window.close()}
              className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Close Window
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Booking Timer */}
      <BookingTimer timerKey={timerKey} expiryTime={expiryTime} />

      {/* Enhanced Header with Glass Effect */}
      <div className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200/50' 
          : 'bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-100'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.close()}
                className="group p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
                title="Close Window"
              >
                <svg className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2L3 7v11a2 2 0 002 2h4v-6h2v6h4a2 2 0 002-2V7l-7-5z" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-gray-900 leading-tight">
                      {hotelDetails.hotelName || 'Hotel Details'}
                    </h1>
                    <div className="flex items-center text-sm text-gray-600 mt-0.5">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span className="truncate max-w-xs">{hotelDetails.overview?.address}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Price Display */}
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Starting from</div>
              <div className="flex items-baseline">
                <span className="text-sm text-gray-500 mr-1">₹</span>
                <span className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  {hotelDetails.overview?.price ? new Intl.NumberFormat('en-IN').format(hotelDetails.overview.price) : 'N/A'}
                </span>
                <span className="text-sm text-gray-500 ml-1">/night</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Navigation Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto scrollbar-hide">
            {[
              { 
                id: 'overview', 
                label: 'Overview', 
                icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
                description: 'Photos & Info'
              },
              { 
                id: 'info-prices', 
                label: 'Rooms & Rates', 
                icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
                description: 'Available Options'
              },
              { 
                id: 'facilities', 
                label: 'Amenities', 
                icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
                description: 'Hotel Features'
              }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200 flex items-center space-x-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className={`p-1.5 rounded-lg transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-blue-100' 
                    : 'bg-gray-100 group-hover:bg-gray-200'
                }`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold">{tab.label}</div>
                  <div className="text-xs text-gray-400">{tab.description}</div>
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Enhanced Content with Better Spacing */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Overview Section with Enhanced Styling */}
          <section id="overview" className="scroll-mt-32">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              <PhotoGallery 
                mainImage={hotelDetails.mainImage}
                images={hotelDetails.images || []}
              />
            </div>
            <div className="mt-6">
              <HotelOverview 
                overview={hotelDetails.overview}
                hotelName={hotelDetails.hotelName}
              />
            </div>
          </section>

          {/* Rooms & Rates Section */}
          <section id="info-prices" className="scroll-mt-32">
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Available Rooms & Rates</h2>
                  <p className="text-gray-600">Choose from our carefully curated room options</p>
                </div>
              </div>
            </div>
            <RoomDetails
              roomDetails={hotelDetails.roomDetails || []}
              roomWiseCancellationPolicy={roomWiseCancellationPolicy}
              onBookNow={handleBookNow}
              isProfileComplete={isProfileComplete}
              isSessionExpired={isSessionExpired}
              hasValidSession={hasValidSession}
            />
          </section>

          {/* Amenities Section */}
          <section id="facilities" className="scroll-mt-32">
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Hotel Amenities & Facilities</h2>
                  <p className="text-gray-600">Discover all the features and services we offer</p>
                </div>
              </div>
            </div>
            <HotelFacilities facilities={hotelDetails.facilities} />
          </section>
        </div>

        {/* Floating Action Button for Quick Booking - Hide if session expired */}
        {hotelDetails.roomDetails && hotelDetails.roomDetails.length > 0 && hasValidSession && (
          <div className="fixed bottom-6 right-6 z-30">
            <button
              onClick={() => {
                const firstRoom = hotelDetails.roomDetails[0];
                if (firstRoom) {
                  handleBookNow(firstRoom);
                }
              }}
              className="group bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl transition-all duration-300 transform hover:scale-110 hover:from-blue-700 hover:to-indigo-700"
              title="Quick Book First Available Room"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <div className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-white">{hotelDetails.roomDetails.length}</span>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onComplete={handleProfileComplete}
        onProfileCompleted={handleProfileCompleted}
      />

      {/* Session Expired Modal */}
      <SessionExpiredModal
        isOpen={showSessionExpiredModal}
        onClose={() => setShowSessionExpiredModal(false)}
      />
    </div>
  );
};

export default HotelDetailsPage;